import { SceneManager } from "./render/SceneManager";
import { buildArena } from "./render/ArenaView";
import { CameraRig } from "./render/CameraRig";
import { NetworkClient } from "./network/NetworkClient";
import { InputManager } from "./input/InputManager";
import { EntityManager } from "./game/EntityManager";
import { SnapshotBuffer } from "./game/SnapshotBuffer";
import { LocalTankController } from "./game/LocalTankController";
import { AssetLoader } from "./assets/AssetLoader";
import { LobbyUI } from "./ui/LobbyUI";
import { HUD } from "./ui/HUD";
import { PauseMenu } from "./ui/PauseMenu";
import { Effects } from "./game/effects";
import { AudioManager } from "./audio/AudioManager";
import { settings } from "./settings";
import {
  INTERP_DELAY_MS, PROJECTILE, POWERUP, ARENA, OBSTACLES, TANK, TICK_DT, ABILITY,
  lerp, angleLerp, clamp,
} from "@tank/shared";

const app = document.getElementById("app")!;
const sm = new SceneManager(app);
const hillMarker = buildArena(sm.scene);

const rig = new CameraRig(sm.camera);
const input = new InputManager();
const net = new NetworkClient();
const assets = new AssetLoader();
const snaps = new SnapshotBuffer();
const local = new LocalTankController();
const hud = new HUD();
const fx = new Effects(sm.scene);
const audio = new AudioManager();
const entities = new EntityManager(sm.scene, assets, fx);

// Damage vignette (red flash when the local tank takes damage).
const dmgFlash = document.createElement("div");
Object.assign(dmgFlash.style, {
  position: "fixed", inset: "0", pointerEvents: "none", zIndex: "15",
  background: "radial-gradient(ellipse at center, transparent 38%, rgba(210,30,30,0.55) 100%)",
  opacity: "0", transition: "opacity 0.18s",
});
document.body.appendChild(dmgFlash);
function flashDamage(): void {
  dmgFlash.style.opacity = "1";
  setTimeout(() => { dmgFlash.style.opacity = "0"; }, 180);
}

window.addEventListener("resize", () => sm.onResize());

// Killfeed.
const killfeed = document.createElement("div");
Object.assign(killfeed.style, {
  position: "fixed", top: "12px", right: "12px", zIndex: "10",
  color: "#fff", font: "13px monospace", whiteSpace: "pre",
  pointerEvents: "none", textShadow: "0 1px 2px #000",
});
document.body.appendChild(killfeed);
const recent: string[] = [];
const addKill = (m: any): void => {
  const killer = stateName(m.killer);
  const victim = stateName(m.victim);
  const line = `${killer} ⚡ ${victim}`;
  recent.push(line);
  if (recent.length > 5) recent.shift();
  killfeed.textContent = recent.join("\n");
  setTimeout(() => {
    const i = recent.indexOf(line);
    if (i >= 0) recent.splice(i, 1);
    killfeed.textContent = recent.join("\n");
  }, 4000);
};
function stateName(id: string): string {
  const p = (net.room?.state as any)?.players?.get(id);
  return p ? String(p.name).slice(0, 10) : String(id).slice(0, 4);
}

// Crosshair (in-game; fixed at screen center). With the point-and-settle aim
// model the world/turret rotate to the aim target, not a cursor position, so
// the reticle itself has no reason to move — it's the fixed reference the
// camera frames the aim direction against (same convention as most
// mouselook third-person cameras).
const crosshair = document.createElement("div");
Object.assign(crosshair.style, {
  position: "fixed", left: "50%", top: "50%", width: "20px", height: "20px",
  transform: "translate(-50%,-50%)", pointerEvents: "none", zIndex: "20", display: "none",
});
crosshair.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20"><path d="M10 2v5M10 13v5M2 10h5M13 10h5" stroke="#ffe600" stroke-width="2.2" stroke-linecap="round"/><circle cx="10" cy="10" r="1.4" fill="#ffe600"/></svg>`;
document.body.appendChild(crosshair);

// "Click to aim" hint: the automatic Pointer Lock request (right after
// joining) can be rejected by the browser if it fires too long after the
// join click's user gesture (e.g. a slow network round-trip) — with no
// visible cursor and no hint, a player would just have working WASD but
// silently-inert aim until they happened to click. Shown/hidden each frame
// based on the actual lock state (see loop()), not just on lock/unlock
// events, so it can never get stuck out of sync.
const clickToAimHint = document.createElement("div");
Object.assign(clickToAimHint.style, {
  position: "fixed", left: "50%", top: "62%", transform: "translateX(-50%)",
  zIndex: "21", display: "none", fontFamily: "system-ui, sans-serif",
  color: "#fff", fontSize: "16px", fontWeight: "600", textShadow: "0 2px 6px #000",
  background: "rgba(10,14,18,0.6)", padding: "8px 16px", borderRadius: "8px",
  pointerEvents: "none",
});
clickToAimHint.textContent = "Cliquez pour activer la visée";
document.body.appendChild(clickToAimHint);

// Pointer Lock: required for the relative-mouse aim to work at all past the
// window's edge. Without it the OS cursor gets pinned at the viewport
// boundary and stops generating further movementX — the turret would get
// visibly "stuck" as soon as you swipe far enough in one direction (the bug
// this fixes). Re-requested on resume/click since the browser silently drops
// the lock on Escape, alt-tab, or losing focus.
function requestLock(): void {
  // requestPointerLock() returns a Promise in modern browsers and rejects
  // (rather than throws) if the browser refuses it (e.g. not close enough to
  // a user gesture, or automation/security policy). Swallow that rejection —
  // the click-to-relock listener below is the fallback, not a hard failure.
  //
  // `unadjustedMovement: true` asks the OS/browser to hand back RAW mouse
  // deltas, bypassing pointer acceleration ("enhance pointer precision" on
  // Windows) — without it, movementX is NOT proportional to actual physical
  // mouse movement: fast flicks report disproportionately larger deltas than
  // slow nudges, which is exactly the "sensitivity feels different fast vs
  // slow" bug. Not all platforms support the flag, so fall back to a plain
  // lock (still functional, just OS-accelerated) if it's rejected for that
  // reason specifically.
  const el = sm.renderer.domElement as HTMLCanvasElement & {
    requestPointerLock: (opts?: { unadjustedMovement?: boolean }) => Promise<void> | void;
  };
  const result = el.requestPointerLock({ unadjustedMovement: true });
  if (result && typeof (result as Promise<void>).catch === "function") {
    (result as Promise<void>).catch((err: unknown) => {
      if (err instanceof Error && err.name === "NotSupportedError") {
        const plain = el.requestPointerLock();
        if (plain && typeof (plain as Promise<void>).catch === "function") {
          (plain as Promise<void>).catch(() => { /* see click-to-relock fallback */ });
        }
      }
      // Other rejections (e.g. not close enough to a user gesture) — the
      // click-to-relock listener below is the fallback, not a hard failure.
    });
  }
}
sm.renderer.domElement.addEventListener("click", () => {
  if (local.initialized && !paused && document.pointerLockElement !== sm.renderer.domElement) requestLock();
});
document.addEventListener("pointerlockchange", () => {
  if (local.initialized && !paused && document.pointerLockElement !== sm.renderer.domElement) {
    setPaused(true); // lock was dropped from under us (Escape/alt-tab/etc.) — treat like opening the menu
  }
});

// Lobby.
const lobby = new LobbyUI(async (r) => {
  lobby.setStatus("Connecting…");
  audio.init(); // user gesture → unlock Web Audio
  audio.setMasterVolume(settings.masterVolume);
  try {
    await net.connect({ name: r.name, mode: r.mode, action: r.action, code: r.code, classId: r.classId });
    lobby.hide();
    hud.setRoomCode(net.roomId, r.mode);
    crosshair.style.display = "block";
    document.body.style.cursor = "none";
    requestLock();
    audio.startAmbient();
    wireState();
  } catch (e: any) {
    lobby.setStatus(e?.message || "Connection failed");
  }
});

// Pause menu (Esc). The server is authoritative and keeps running, so "pause"
// = free the cursor + show the overlay + stop sending input (the tank holds
// still server-side). Settings sliders mutate `settings` live and persist.
const pauseMenu = new PauseMenu(
  () => setPaused(false),
  () => { try { net.room?.leave(); } catch { /* already gone */ } location.reload(); },
  () => audio.setMasterVolume(settings.masterVolume),
);
let paused = false;
function setPaused(p: boolean): void {
  if (p === paused) return;
  paused = p;
  if (p) {
    pauseMenu.show();
    document.body.style.cursor = "auto";
    crosshair.style.display = "none";
    if (document.pointerLockElement === sm.renderer.domElement) document.exitPointerLock();
  } else {
    pauseMenu.hide();
    document.body.style.cursor = "none";
    crosshair.style.display = "block";
    requestLock();
  }
}
window.addEventListener("keydown", (e) => {
  if (e.code === "Escape" && local.initialized) {
    e.preventDefault();
    setPaused(!paused);
  }
});

function wireState() {
  const cb = net.callbacks!;
  const myId = net.sessionId;
  cb.onAdd("players", (p: any, id: string) => entities.ensureTank(id, p.color as number, id === myId, String(p.classId || "balanced")));
  cb.onRemove("players", (_p: any, id: string) => entities.removeTank(id));
  cb.onAdd("projectiles", (p: any, id: string) => {
    if (p.ownerId === myId) return; // predicted locally
    entities.ensureProjectile(id, +p.x, +p.y, +p.z, !!p.ricochet);
  });
  cb.onRemove("projectiles", (p: any, id: string) => {
    if (p) {
      fx.impact(+p.x, +p.y, +p.z);
      const sp = surfaceHitPoint(+p.x, +p.z);
      if (sp) fx.impactDecal(sp.x, +p.y, sp.z, +p.vx, +p.vz);
    }
    entities.removeProjectile(id);
  });
  cb.onAdd("powerups", (pu: any, id: string) => {
    entities.ensurePowerUp(id, pu.type, +pu.x, +pu.z, !!pu.active);
    cb.listen(pu, "active", (v) => entities.setPowerUpActive(id, !!v));
  });
  cb.onRemove("powerups", (_pu: any, id: string) => entities.removePowerUp(id));
  // Coop wave announcements (deathmatch's `wave` field just stays 0, so this
  // never fires there). `.listen` invokes once immediately with the current
  // value on attach — showWaveBanner no-ops on wave < 1, so that initial call
  // (before any wave has actually started) is harmless.
  cb.listen(net.room!.state as any, "wave", (v: number) => hud.showWaveBanner(v));
  net.room!.onMessage("kill", (m: any) => {
    addKill(m);
    if (m.killer === myId) { rig.addShake(0.9); audio.explosion(); } // local-kill punch
  });
  net.room!.onMessage("roundEnd", (m: any) => hud.showRoundEnd(m.reason));

  // ── Ability FX (server-broadcast so every client sees the same event) ──
  // Lobbed mortar: render the incoming shell + ground warning, then the boom.
  net.room!.onMessage("mortarWarn", (m: any) => fx.mortarIncoming(+m.sx, +m.sz, +m.tx, +m.tz, +m.travel));
  net.room!.onMessage("mortar", (m: any) => {
    fx.bigExplosion(+m.x, 1.2, +m.z);
    rig.addShake(1.3);
    audio.mortarImpact();
  });
  // bot_ricochet shots bouncing off a wall/obstacle — a small spark so the
  // bounce reads as an actual impact, not a shot silently changing direction.
  net.room!.onMessage("ricochet", (m: any) => fx.impact(+m.x, 1.0, +m.z));
  // Remote cast SFX (the caster predicts its own on keypress).
  net.room!.onMessage("cast", (m: any) => {
    if (m.id === myId) return; // local cast SFX is predicted on keypress
    const kind = String(m.kind);
    if (kind === "repair") audio.repair();
    else if (kind === "bulwark") audio.bulwark();
    else if (kind === "dash") audio.dash();
    else if (kind === "mortar") audio.mortarLaunch();
  });
}

interface LocalProj { x: number; y: number; z: number; vx: number; vz: number; life: number; }
const localProjectiles: LocalProj[] = [];
let lastLocalShot = 0;
const prevAlive = new Map<string, boolean>();
let prevHp = 0;
let prevEffects = 0;

/** If (x,z) is at/near a wall, obstacle, or the arena boundary, returns the
 * clamped point ON that surface (not null); otherwise null. Used to gate
 * persistent scorch decals to actual surface hits (not tank hits/TTL expiry,
 * which shouldn't leave a mark on nothing) AND to place the decal exactly on
 * the boundary — the death-detecting position can be up to a tick's travel
 * (~0.5m) past the true surface, which would otherwise bury the decal inside
 * the solid geometry, hidden from outside. */
function surfaceHitPoint(x: number, z: number): { x: number; z: number } | null {
  const limit = ARENA.HALF - ARENA.WALL_THICKNESS / 2;
  if (Math.abs(x) >= limit - 0.5 || Math.abs(z) >= limit - 0.5) {
    return { x: clamp(x, -limit, limit), z: clamp(z, -limit, limit) };
  }
  for (const o of OBSTACLES) {
    if (Math.abs(x - o.x) <= o.w / 2 + 0.3 && Math.abs(z - o.z) <= o.d / 2 + 0.3) {
      return { x: clamp(x, o.x - o.w / 2, o.x + o.w / 2), z: clamp(z, o.z - o.d / 2, o.z + o.d / 2) };
    }
  }
  return null;
}
// Aim model: point-and-settle. Raw mouse MOVEMENT (not cursor position) accumulates
// into `aimTargetAngle` at a fixed sensitivity — moving the mouse turns the target
// by that much and STOPS the instant the mouse stops (no continuous re-derivation).
// The turret (and, via CameraRig, the camera) then eases toward that static target
// at settings.aimRate and settles — matching "point somewhere, it turns there, then
// stops" instead of the old cursor-on-ground-plane raycast, which fed back into the
// very camera it was computed through (camera swings to follow aim -> the ground
// point under a STATIONARY cursor shifts because the camera moved -> aim target
// shifts again -> camera never quite settles). Framerate-independent (a pixel of
// mouse movement always yields the same angle regardless of frame rate).
const MOUSE_SENS = 0.0018; // rad per pixel of raw mouse movement, before aimRate scaling
let aimTargetAngle = 0;
// Mortar landing distance: no cursor position to derive it from anymore, so
// the scroll wheel adjusts it directly (accumulated the same delta-based way
// as aim). Starts at a comfortable mid-range distance.
const WHEEL_SENS = 0.03; // meters of aim distance per wheel "deltaY" unit
let localAimDist: number = (ABILITY.MORTAR_MIN_RANGE + ABILITY.MORTAR_RANGE) / 2;

// Render interpolation for the local tank ("Fix Your Timestep" pattern): we
// keep the tank's position from the END of the PREVIOUS fixed tick
// (prevLocalX/Z/Rot) and blend towards the just-simulated CURRENT tick
// (local.x/z/rotY) using alpha = (leftover accumulator) / TICK_MS — i.e. how
// far real time has progressed past the last tick, towards the next one.
// This renders a constant-velocity glide between two known simulation states
// every frame, instead of an exponential filter chasing a value that only
// changes once every ~33ms: that filter produced a fast catch-up pulse right
// after each tick followed by a near-stationary hold until the next one —
// visible as a rhythmic micro "shake" whenever the tank was moving (confirmed
// by instrumenting rX/rZ frame deltas: they spiked ~0.2-0.3 right after each
// tick then decayed to ~0.03 before the next, instead of a flat ~0.11/frame).
let rX = 0, rZ = 0, rRot = 0, rInit = false;
let prevLocalX = 0, prevLocalZ = 0, prevLocalRot = 0;

// Movement is simulated on a FIXED timestep matching the server's tick rate
// (TICK_DT). One fixed step == one input sent == one server tick consumed, so
// LocalTankController's replay-based reconciliation is exact (see ArenaRoom's
// per-player input queue). Rendering itself stays at the display's own frame
// rate — only body/turret position are INTERPOLATED (rX/rZ/rRot) to hide the
// 30Hz steps.
const TICK_MS = 1000 * TICK_DT;
const MAX_STEPS_PER_FRAME = 5; // avoid a catch-up spiral after a stall (tab throttled, GC pause, etc.)
const CAPTURE_INTERVAL_MS = 33; // ~30 Hz snapshot capture (patches arrive at 20 Hz)
let last = performance.now();
let simAcc = 0;
let lastCapture = 0;

function loop(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;

  // Surface the "click to aim" hint whenever we're in-game, unpaused, but the
  // mouse isn't actually captured — checked every frame (not just on
  // lock/unlock events) so it can never end up stuck out of sync.
  clickToAimHint.style.display =
    local.initialized && !paused && document.pointerLockElement !== sm.renderer.domElement ? "block" : "none";

  const roomState = net.room?.state as any;
  const players = roomState?.players;
  const me = players?.get(net.sessionId);
  if (!local.initialized && me) {
    local.init(me.x, me.z, me.rotY);
    prevLocalX = local.x;
    prevLocalZ = local.z;
    prevLocalRot = local.rotY;
  }
  if (me) local.setClass(me.classId); // data-driven: prediction matches the server class

  // Tank deaths → explosion FX + sound + shake; local damage/pickup feedback.
  if (players) {
    players.forEach((p: any, id: string) => {
      const alive = !!p.alive;
      if (prevAlive.get(id) === true && !alive) {
        fx.explosion(+p.x, 1.2, +p.z, 1.5);
        audio.explosion();
        rig.addShake(id === net.sessionId ? 1.4 : 0.5);
      }
      prevAlive.set(id, alive);
    });
  }
  if (me) {
    const hp = +me.hp;
    const eff = +me.effects;
    if (prevHp && hp < prevHp) {
      audio.hit();
      rig.addShake(0.25);
      flashDamage();
    }
    if (hp > prevHp || eff & ~prevEffects) audio.pickup();
    prevHp = hp;
    prevEffects = eff;
  }

  if (local.initialized) {
    const cls = local.cls;
    const hasSpeed = !!me && (me.effects & 1);
    const hasRapid = !!me && (me.effects & 2);
    const speedMult = hasSpeed ? POWERUP.SPEED_MULT : 1;

    // Fixed-timestep movement: exactly matches the server's TICK_DT so one
    // client input == one server tick, making applyServer()'s replay exact
    // (no more heuristic drift-threshold snapping mid-drive).
    simAcc += dt * 1000;
    let steps = 0;
    while (simAcc >= TICK_MS && steps < MAX_STEPS_PER_FRAME) {
      prevLocalX = local.x;
      prevLocalZ = local.z;
      prevLocalRot = local.rotY;
      // Paused → send zeroed input so the tank holds still server-side (the
      // server otherwise re-applies the last held input).
      const mv = paused ? { fwd: 0, turn: 0 } : input.getMove();
      const firing = paused ? false : input.isFiring();
      local.seq++;
      local.stepInput(local.seq, mv.fwd, mv.turn, speedMult);
      net.sendInput(local.seq, mv.fwd, mv.turn, local.turretAngle, localAimDist, firing);
      simAcc -= TICK_MS;
      steps++;
    }
    if (steps === MAX_STEPS_PER_FRAME) simAcc = 0;

    // While paused the cursor is free (for the menu) — don't aim, fire, or cast.
    if (!paused) {
    // Accumulate raw mouse movement into the (static-until-moved) aim target,
    // then ease the turret toward it — see the point-and-settle comment above.
    // Sign: this world's yaw convention (forward = sin(rotY), cos(rotY)) turns
    // the chase-cam view LEFT as rotY increases, so mouse-right (dx>0) must
    // SUBTRACT from the target to turn the view right (confirmed empirically
    // — the "+" version was inverted: moving the mouse left turned the view
    // right).
    const dx = input.consumeMouseDeltaX();
    aimTargetAngle -= dx * MOUSE_SENS;
    const aimK = 1 - Math.exp(-dt * settings.aimRate);
    local.turretAngle = angleLerp(local.turretAngle, aimTargetAngle, aimK);

    // Mortar landing distance: scroll wheel nudges it directly (no cursor
    // position to derive it from with Pointer Lock engaged).
    const wheelDy = input.consumeWheelDeltaY();
    localAimDist = clamp(
      localAimDist - wheelDy * WHEEL_SENS,
      ABILITY.MORTAR_MIN_RANGE, ABILITY.MORTAR_RANGE,
    );

    // Local fire prediction (instant feedback), class-driven cooldown/speed.
    const cd = hasRapid ? cls.fireCooldownMs * POWERUP.COOLDOWN_MULT : cls.fireCooldownMs;
    if (input.isFiring() && now - lastLocalShot >= cd) {
      lastLocalShot = now;
      const a = local.turretAngle;
      const mx = local.x + Math.sin(a) * TANK.MUZZLE_FORWARD;
      const mz = local.z + Math.cos(a) * TANK.MUZZLE_FORWARD;
      localProjectiles.push({
        x: mx, y: TANK.MUZZLE_Y, z: mz,
        vx: Math.sin(a) * cls.projectileSpeed, vz: Math.cos(a) * cls.projectileSpeed,
        life: cls.projectileTtlMs / 1000,
      });
      fx.muzzleFlash(mx, TANK.MUZZLE_Y, mz);
      audio.shoot();
      rig.addShake(0.2);
    }

    // Class special (Shift). Always send the intent (server is authoritative);
    // gate the predicted SFX/FX on the server clock so we don't play a cast
    // that's still on cooldown (and so a post-respawn reset reads as ready).
    if (input.consumeAbility()) {
      const clock = +(roomState?.clock ?? 0);
      const ready = !me || +me.abilityCdUntil <= clock;
      net.sendAbility();
      if (ready) {
        const kind = local.cls.ability.id;
        if (kind === "repair") audio.repair();
        else if (kind === "bulwark") audio.bulwark();
        else if (kind === "dash") { audio.dash(); fx.dashTrail(rX, rZ, rRot); }
        else if (kind === "mortar") audio.mortarLaunch();
      }
    }
    } // end !paused

    if (me) local.applyServer(me.x, me.z, me.rotY, me.lastSeq);

    // Interpolate the displayed body between the last two simulated ticks
    // (turret stays instant). `alpha` is how far the accumulator has
    // progressed towards the next not-yet-simulated tick — using it to blend
    // prevLocal→local gives a constant-velocity glide with no catch-up pulse,
    // regardless of display refresh rate.
    if (!rInit) { rX = local.x; rZ = local.z; rRot = local.rotY; rInit = true; }
    const alpha = Math.max(0, Math.min(1, simAcc / TICK_MS));
    rX = lerp(prevLocalX, local.x, alpha);
    rZ = lerp(prevLocalZ, local.z, alpha);
    rRot = angleLerp(prevLocalRot, local.rotY, alpha);
  }

  // Advance + cull local projectiles (walls / obstacles / enemies).
  const wallLimit = ARENA.HALF - ARENA.WALL_THICKNESS / 2;
  const hitR = local.cls.radius + PROJECTILE.RADIUS;
  for (let i = localProjectiles.length - 1; i >= 0; i--) {
    const p = localProjectiles[i];
    p.x += p.vx * dt;
    p.z += p.vz * dt;
    p.life -= dt;
    let dead = p.life <= 0;
    let hitSurface = false; // wall/obstacle/boundary — leaves a persistent scorch decal
    if (!dead && (Math.abs(p.x) >= wallLimit || Math.abs(p.z) >= wallLimit)) { dead = true; hitSurface = true; }
    if (!dead) {
      for (const o of OBSTACLES) {
        if (Math.abs(p.x - o.x) <= o.w / 2 && Math.abs(p.z - o.z) <= o.d / 2) { dead = true; hitSurface = true; break; }
      }
    }
    if (!dead && players) {
      players.forEach((tp: any, tid: string) => {
        if (dead || tid === net.sessionId || !tp.alive) return;
        if (Math.hypot(p.x - +tp.x, p.z - +tp.z) < hitR) dead = true;
      });
    }
    if (dead) {
      fx.impact(p.x, p.y, p.z);
      if (hitSurface) {
        const sp = surfaceHitPoint(p.x, p.z) ?? { x: p.x, z: p.z };
        fx.impactDecal(sp.x, p.y, sp.z, p.vx, p.vz);
      }
      localProjectiles.splice(i, 1);
    }
  }


  if (players && now - lastCapture >= CAPTURE_INTERVAL_MS) {
    snaps.capture(now, players);
    lastCapture = now;
  }
  const remote = snaps.sample(now - INTERP_DELAY_MS);

  entities.update({
    remoteTanks: remote,
    localId: net.sessionId,
    local: local.initialized
      ? { x: rX, z: rZ, rotY: rRot, turretAngle: local.turretAngle, alive: me?.alive ?? true, effects: +(me?.effects ?? 0) }
      : null,
    projectiles: roomState?.projectiles,
    localProjectiles,
    now,
  });

  // Camera trails the AIM direction (not the hull) — see CameraRig.
  if (local.initialized) rig.follow(rX, rZ, local.turretAngle, dt);

  fx.update(dt);
  if (roomState) hud.update(me, players, roomState, net.sessionId, now);

  // King of the Hill zone marker: hidden outside koth, tinted by whichever
  // tank currently holds it (reusing that tank's own synced color) or a
  // neutral gray when unclaimed/contested.
  if (roomState?.mode === "koth") {
    hillMarker.group.visible = true;
    const holderId = String(roomState.hillHolderId ?? "");
    const holder = holderId ? players?.get(holderId) : null;
    hillMarker.setColor(holder ? Number(holder.color) : 0xaaaaaa);
  } else {
    hillMarker.group.visible = false;
  }

  sm.render();
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
